import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { formatCurrency, formatTime } from '../utils/formatters';

// Spot price over the course of a break's fill: every sale in the order it
// sold (x = sale #, y = price), with the running average beside it.
//
// Trade-ins don't appear as events. The two spots a customer traded in were
// real sales and stay as points. The summed trade-in price isn't a sale and
// isn't plotted. When the emptied teams sell again, those resales are ordinary
// points that the tooltip labels. So a break with trade-ins shows more points
// than it has spots.
//
// `live` joins the break's /ws/breaks room and refetches on every spots_update,
// so the chart grows as the board fills.

const PRICE_COLOR = '#3987e5';
const AVG_COLOR = '#8a94a6';

function SaleTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Sale #{p.n} · {p.team}</div>
      <div>{formatCurrency(p.price)}{p.customer ? ` — ${p.customer}` : ''}</div>
      <div style={{ color: 'var(--text-light)' }}>Avg so far {formatCurrency(p.running_avg)}</div>
      {p.sold_at && <div style={{ color: 'var(--text-light)' }}>{formatTime(p.sold_at.slice(11))}</div>}
      {p.is_resale && <div style={{ color: 'var(--text-light)', marginTop: 2 }}>Resale — team came back after a trade-in</div>}
      {p.traded_in && <div style={{ color: 'var(--text-light)', marginTop: 2 }}>Later traded in</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ minWidth: 64 }}>
      <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

// `compact` is for the break sidebars: one line of stats, a short plot, and
// the legend and notes folded into that line.
export default function BreakPriceTimeline({ breakId, live = false, height = 220, compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const refetchTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/api/breaks/${breakId}/sales-timeline`);
      setData(d);
      setError(null);
    } catch {
      setError('Could not load the price timeline');
    }
  }, [breakId]);

  useEffect(() => {
    if (!breakId) return;
    setData(null);
    load();
  }, [breakId, load]);

  useEffect(() => {
    if (!live || !breakId) return;
    let closed = false;
    let socket = null;
    let retry = null;
    // A burst of saves while the board fills needs one refetch, not ten.
    const scheduleLoad = () => {
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(load, 300);
    };
    function connect(isReconnect) {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws/breaks`);
      socket = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'join', breakId }));
        if (isReconnect) scheduleLoad();
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'ping') { ws.send(JSON.stringify({ type: 'pong' })); return; }
          if (msg.type === 'spots_update' && String(msg.breakId) === String(breakId)) scheduleLoad();
        } catch { /* not ours */ }
      };
      ws.onclose = () => {
        if (closed || socket !== ws) return;
        retry = setTimeout(() => connect(true), 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* onclose handles it */ } };
    }
    connect(false);
    return () => {
      closed = true;
      clearTimeout(retry);
      clearTimeout(refetchTimer.current);
      try { socket?.close(); } catch { /* already gone */ }
    };
  }, [live, breakId, load]);

  if (error) return <div style={{ fontSize: 13, color: 'var(--text-light)' }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Loading…</div>;

  const points = data.points || [];
  if (!points.length) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-light)', padding: '8px 0' }}>
        No spot sales logged yet{live ? ' — the chart fills in as spots sell.' : '.'}
      </div>
    );
  }

  const prices = points.map(p => p.price);
  const first = points[0];
  const last = points[points.length - 1];
  const high = Math.max(...prices);
  const low = Math.min(...prices);
  const resales = points.filter(p => p.is_resale).length;

  const chart = (
    <ResponsiveContainer width="100%" height={compact ? 110 : height}>
      <LineChart data={points} margin={compact ? { top: 4, right: 8, left: 0, bottom: 0 } : { top: 8, right: 12, left: 0, bottom: 4 }}>
        <CartesianGrid vertical={false} stroke="var(--border)" />
        <XAxis dataKey="n" tick={{ fontSize: compact ? 10 : 11 }} allowDecimals={false}
          height={compact ? 18 : 30} minTickGap={compact ? 12 : 5}
          label={compact ? undefined : { value: 'Sale #', position: 'insideBottomRight', offset: -2, fontSize: 11 }} />
        <YAxis tick={{ fontSize: compact ? 10 : 11 }} width={compact ? 38 : 48} tickCount={compact ? 3 : 5}
          tickFormatter={v => `$${v}`} />
        <Tooltip content={<SaleTooltip />} />
        <Line type="linear" dataKey="running_avg" stroke={AVG_COLOR} strokeWidth={compact ? 1.5 : 2} strokeDasharray="4 3"
          dot={false} activeDot={false} isAnimationActive={false} />
        <Line type="linear" dataKey="price" stroke={PRICE_COLOR} strokeWidth={2}
          dot={{ r: compact ? 2 : 3, fill: PRICE_COLOR, strokeWidth: 0 }} activeDot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );

  if (compact) {
    const meta = [
      `${points.length} sale${points.length === 1 ? '' : 's'}`,
      `first ${formatCurrency(first.price)}`,
      `last ${formatCurrency(last.price)}`,
      `high ${formatCurrency(high)}`,
      `low ${formatCurrency(low)}`,
      `avg ${formatCurrency(last.running_avg)}`,
    ].join(' · ');
    const notes = [
      resales > 0 && `incl. ${resales} resale${resales === 1 ? '' : 's'} after trade-ins`,
      data.reconstructed && 'approximate (rebuilt)',
    ].filter(Boolean).join(' · ');
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-light)', marginBottom: 2 }}>
          <span>{meta}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 2, background: PRICE_COLOR, display: 'inline-block' }} /> price
            <span style={{ width: 12, borderTop: `2px dashed ${AVG_COLOR}`, display: 'inline-block', marginLeft: 6 }} /> avg
          </span>
        </div>
        {chart}
        {notes && <div style={{ fontSize: 10, color: 'var(--text-light)' }}>{notes}</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        {/* A count of sales, not spots: a traded-in team that sells again is
            a second sale, so this can run past the spot total. */}
        <Stat label="Sales" value={points.length} />
        <Stat label="First" value={formatCurrency(first.price)} />
        <Stat label="Last" value={formatCurrency(last.price)} />
        <Stat label="High" value={formatCurrency(high)} />
        <Stat label="Low" value={formatCurrency(low)} />
        <Stat label="Avg" value={formatCurrency(last.running_avg)} />
      </div>
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--text-light)', marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 14, height: 2, background: PRICE_COLOR, display: 'inline-block' }} /> Sale price
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 14, borderTop: `2px dashed ${AVG_COLOR}`, display: 'inline-block' }} /> Running avg
        </span>
      </div>
      {chart}
      {(resales > 0 || data.reconstructed) && (
        <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
          {resales > 0 && `${resales} resale${resales === 1 ? '' : 's'} after trade-ins included. `}
          {data.reconstructed && 'Rebuilt from saved spot times — approximate. Spots priced before timestamps existed are left out.'}
        </div>
      )}
    </div>
  );
}
